import React from 'react';
import styled from 'styled-components';

import { useSelector } from 'react-redux';
import { showTelnyxFeatures, State } from '@store/index';
import { DocumentLoader } from '@common/hooks/useDocument';
import { checkDeletedFiles, refreshLoader, LoaderConfiguration } from '@requests/loaders';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { DevdocsLoader } from './Devdocs';
import { IntercomLoader } from './Intercom';
import { ContentfulLoader } from './Contentful';
import { GuruLoader } from './Guru';
import { SlackLoader } from './Slack';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 0.75rem;
  overflow-y: auto;
  max-height: calc(100vh - 61px - 91px - 24px);
`;

export const Group = styled.div<{ isAllOpen?: boolean; padding?: string }>`
  display: flex;
  flex-direction: column;
  justify-content: center;

  flex-grow: ${({ isAllOpen }) => (isAllOpen ? 1 : 'unset')};
  border: 1px solid ${({ theme }) => theme.line.light};
  border-radius: 8px;
  overflow: hidden;
  gap: 0.75rem;
  padding: ${({ padding }) => (padding ? padding : 0)};

  min-height: calc(65px - 32px);

  h4 {
    font-family: 'PPFormula';
    font-size: 14px;
    text-transform: lowercase;
    padding-top: 4px;
    font-weight: 600;
  }

  p {
    color: #00000099;
    font-size: 14px;
    font-family: 'Inter';
    margin-bottom: 4px;
  }

  .content {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .logo {
    display: flex;
    align-items: center;
    justify-content: center;
    /* max-width: 20px; */
    min-width: 20px;
    width: 20px;
  }
`;

interface Props {
  loader: LoaderConfiguration | undefined;
  loaderStatus: string;
  page: number;
  loaderSettingsOpen: string | null;
  setLoaderSettingsOpen: React.Dispatch<React.SetStateAction<string | null>>;
  setRemoveLoader: React.Dispatch<React.SetStateAction<string | null>>;
  documentLoader: DocumentLoader;
  setTab: React.Dispatch<React.SetStateAction<string>>;
}

export const Loader: React.FC<Props> = ({
  loader,
  loaderStatus,
  page,
  documentLoader,
  loaderSettingsOpen,
  setLoaderSettingsOpen,
  setTab,
  setRemoveLoader,
}) => {
  const queryClient = useQueryClient();

  const isTelnyxUser = useSelector((state: State) => showTelnyxFeatures(state));

  const { mutateAsync: refresh } = useMutation({
    mutationFn: refreshLoader,
    onSuccess: (data): void => {
      queryClient.invalidateQueries({ queryKey: ['globalFiles', 'bucket-loader'] });
      if (data?.error) console.log('Error updating refresh time', data);
    },
  });

  const { mutateAsync: checkForDeletedFiles } = useMutation({
    mutationFn: checkDeletedFiles,
    onSuccess: (data): void => {
      queryClient.invalidateQueries({ queryKey: ['globalFiles'] });
      if (data?.error) console.log('Error updating refresh time', data);
    },
  });

  const loaderNotConfigured = !loader && loaderStatus === 'success';
  const showIntercom = loaderNotConfigured || loader?.loader === 'intercom';
  const showContentful = loaderNotConfigured || loader?.loader === 'contentful';
  const showDevdocs = loaderNotConfigured || loader?.loader === 'devdocs';
  const showGuru = loaderNotConfigured || loader?.loader === 'guru';
  const showSlack = loaderNotConfigured || loader?.loader === 'slack';

  return (
    <Container>
      {showDevdocs && isTelnyxUser && (
        <DevdocsLoader
          devdocsLoaderPage={page}
          loader={loader}
          open={loaderSettingsOpen}
          setOpen={setLoaderSettingsOpen}
          documentLoader={documentLoader}
          setTab={setTab}
          refreshLoader={refresh}
          setRemoveLoader={setRemoveLoader}
          checkForDeletedFiles={checkForDeletedFiles}
        />
      )}
      {showIntercom && (
        <IntercomLoader
          intercomLoaderPage={page}
          loader={loader}
          open={loaderSettingsOpen}
          setOpen={setLoaderSettingsOpen}
          documentLoader={documentLoader}
          setTab={setTab}
          refreshLoader={refresh}
          setRemoveLoader={setRemoveLoader}
          checkForDeletedFiles={checkForDeletedFiles}
        />
      )}
      {showContentful && (
        <ContentfulLoader
          loader={loader}
          open={loaderSettingsOpen}
          setOpen={setLoaderSettingsOpen}
          documentLoader={documentLoader}
          contentfulLoaderPage={page}
          setTab={setTab}
          refreshLoader={refresh}
          setRemoveLoader={setRemoveLoader}
          checkForDeletedFiles={checkForDeletedFiles}
        />
      )}
      {showGuru && (
        <GuruLoader
          page={page}
          loader={loader}
          open={loaderSettingsOpen}
          setOpen={setLoaderSettingsOpen}
          documentLoader={documentLoader}
          setTab={setTab}
          refreshLoader={refresh}
          setRemoveLoader={setRemoveLoader}
          checkForDeletedFiles={checkForDeletedFiles}
        />
      )}
      {showSlack && isTelnyxUser && (
        <SlackLoader
          page={page}
          loader={loader}
          open={loaderSettingsOpen}
          setOpen={setLoaderSettingsOpen}
          documentLoader={documentLoader}
          setTab={setTab}
          refreshLoader={refresh}
          setRemoveLoader={setRemoveLoader}
          checkForDeletedFiles={checkForDeletedFiles}
        />
      )}
    </Container>
  );
};
